import React, { useCallback, useMemo, useState } from 'react'

import { Check, ChevronsUpDown } from 'lucide-react'

import type { TagWithColor } from '@/components/feature/TestAsset/utils'
import { Badge } from '@/components/ui/badge'
import { buttonVariants } from '@/components/ui/button'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/components/ui/command'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import type { Tag } from '@/shared/types'

export interface EditorTagAutocompleteProps {
	tags: Tag[]
	availableTags: TagWithColor[]
	setTags: (tags: string[]) => void
	disabled?: boolean
	className?: string
}

export const EditorTagAutocomplete: React.FC<EditorTagAutocompleteProps> = ({
	tags,
	availableTags,
	setTags,
	disabled = true,
	className,
}) => {
	const [open, setOpen] = useState(false)
	const selectedTags = useMemo(
		() => new Set(tags.map(tag => tag._id)),
		[tags]
	)

	const handleSelect = useCallback(
		(tagId: string) => {
			const next = new Set(selectedTags)
			if (next.has(tagId)) next.delete(tagId)
			else next.add(tagId)
			setTags(Array.from(next))
		},
		[selectedTags, setTags]
	)

	const options = useMemo(
		() =>
			availableTags
				.filter(tag => tag.status !== 'inactive')
				.map(tag => ({
					label: tag.name,
					value: tag._id,
					color: tag.color,
				})),
		[availableTags]
	)

	return (
		<div
			className={cn(
				'flex items-center justify-end gap-2 sm:justify-start',
				className
			)}
		>
			<span className="text-sm font-medium">Tags:</span>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger
					className={cn(
						buttonVariants({ variant: 'outline' }),
						'w-40 justify-between'
					)}
					role="combobox"
					aria-label="Tags"
					aria-expanded={open}
					disabled={disabled}
				>
					<span className="truncate">
						{selectedTags.size > 0
							? `${selectedTags.size} selected`
							: 'Select tags...'}
					</span>
					<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
				</PopoverTrigger>
				<PopoverContent className="w-48 p-0">
					<Command>
						<CommandInput placeholder="Search tags..." />
						<CommandList>
							<CommandEmpty>No tags found.</CommandEmpty>
							<CommandGroup>
								{options.map(option => (
									<CommandItem
										key={option.value}
										value={option.label} // Use label for searching
										onSelect={() =>
											handleSelect(option.value)
										}
									>
										<Check
											className={cn(
												'mr-2 h-4 w-4',
												selectedTags.has(option.value)
													? 'opacity-100'
													: 'opacity-0'
											)}
										/>
										<Badge
											style={{
												backgroundColor: option.color,
											}}
											className="border-transparent text-primary-foreground"
										>
											{option.label}
										</Badge>
									</CommandItem>
								))}
							</CommandGroup>
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>
		</div>
	)
}
